"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  PULL_TO_REFRESH_SETTLE_MS,
  PULL_TO_REFRESH_THRESHOLD_PX,
  applyPullToRefreshSurface,
  canStartPullToRefresh,
  clearPullToRefreshSurface,
  getPullToRefreshPhysics,
  getPullToRefreshStatus,
  isElementScrolledFromTop,
  isPullToRefreshSurfaceActive,
  remainingPullToRefreshHoldMs,
  shouldIgnorePullGesture,
  shouldReleasePullToRefreshTracking,
  type PullToRefreshStatus,
} from "../pwa";

export interface UsePullToRefreshOptions {
  onRefresh: () => void | Promise<void>;
  enabled?: boolean;
  thresholdPx?: number;
}

export interface UsePullToRefreshResult {
  status: PullToRefreshStatus;
  offset: number;
  progress: number;
}

function getTouchPoint(event: TouchEvent): { x: number; y: number } | null {
  const touch = event.touches[0] ?? event.changedTouches[0];
  if (!touch) {
    return null;
  }
  return { x: touch.clientX, y: touch.clientY };
}

function ancestorIsScrolledFromTop(target: EventTarget | null): boolean {
  let node: Node | null = target instanceof Node ? target : null;
  const scrollTops: number[] = [];

  while (node && node !== document.body && node !== document.documentElement) {
    if (node instanceof HTMLElement) {
      scrollTops.push(node.scrollTop);
    }
    node = node.parentNode;
  }

  return scrollTops.some((scrollTop) => isElementScrolledFromTop(scrollTop));
}

function syncPullToRefreshSurface(
  pullDistancePx: number,
  isRefreshing: boolean,
  settling: boolean,
  thresholdPx: number,
): void {
  const distance = isRefreshing ? thresholdPx : pullDistancePx;
  const physics = getPullToRefreshPhysics(distance, thresholdPx);
  const status = getPullToRefreshStatus(isRefreshing, distance, thresholdPx);
  applyPullToRefreshSurface(document.documentElement, {
    offset: physics.offset,
    progress: physics.progress,
    active: isPullToRefreshSurfaceActive(status, settling),
    settling,
  });
}

export function usePullToRefresh({
  onRefresh,
  enabled = true,
  thresholdPx = PULL_TO_REFRESH_THRESHOLD_PX,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [settling, setSettling] = useState(false);

  const onRefreshRef = useRef(onRefresh);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const settlingRef = useRef(false);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);
  const trackingRef = useRef(false);
  const settleTimerRef = useRef<number | null>(null);

  onRefreshRef.current = onRefresh;
  pullDistanceRef.current = pullDistance;
  isRefreshingRef.current = isRefreshing;
  settlingRef.current = settling;

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const beginSettle = useCallback(() => {
    settlingRef.current = true;
    setSettling(true);
    syncPullToRefreshSurface(0, false, true, thresholdPx);
    clearSettleTimer();
    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null;
      settlingRef.current = false;
      setSettling(false);
    }, PULL_TO_REFRESH_SETTLE_MS);
  }, [clearSettleTimer, thresholdPx]);

  const resetPull = useCallback(() => {
    trackingRef.current = false;
    startPointRef.current = null;
    pullDistanceRef.current = 0;
    setPullDistance(0);
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearPullToRefreshSurface(document.documentElement);
      return;
    }

    syncPullToRefreshSurface(
      pullDistance,
      isRefreshing,
      settling,
      thresholdPx,
    );
  }, [enabled, isRefreshing, pullDistance, settling, thresholdPx]);

  useEffect(() => {
    return () => {
      clearSettleTimer();
      clearPullToRefreshSurface(document.documentElement);
    };
  }, [clearSettleTimer]);

  useEffect(() => {
    if (!enabled) {
      clearSettleTimer();
      settlingRef.current = false;
      setSettling(false);
      resetPull();
      return;
    }

    let gestureListenersAttached = false;

    const detachGestureListeners = () => {
      if (!gestureListenersAttached) {
        return;
      }
      gestureListenersAttached = false;
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", finishGesture);
      window.removeEventListener("touchcancel", finishGesture);
    };

    const attachGestureListeners = () => {
      if (gestureListenersAttached) {
        return;
      }
      gestureListenersAttached = true;
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", finishGesture);
      window.addEventListener("touchcancel", finishGesture);
    };

    function onTouchStart(event: TouchEvent) {
      if (isRefreshingRef.current || event.touches.length !== 1) {
        return;
      }

      const point = getTouchPoint(event);
      if (!point) {
        return;
      }

      const canStart = canStartPullToRefresh({
        windowScrollY: window.scrollY,
        documentScrollTop: document.documentElement.scrollTop,
        ancestorScrolledFromTop: ancestorIsScrolledFromTop(event.target),
      });

      if (!canStart) {
        detachGestureListeners();
        startPointRef.current = null;
        trackingRef.current = false;
        return;
      }

      clearSettleTimer();
      settlingRef.current = false;
      setSettling(false);
      startPointRef.current = point;
      trackingRef.current = false;
      attachGestureListeners();
    }

    function onTouchMove(event: TouchEvent) {
      if (isRefreshingRef.current || !startPointRef.current) {
        return;
      }

      const point = getTouchPoint(event);
      if (!point) {
        return;
      }

      const dx = point.x - startPointRef.current.x;
      const dy = point.y - startPointRef.current.y;

      if (!trackingRef.current) {
        if (shouldReleasePullToRefreshTracking(dx, dy)) {
          startPointRef.current = null;
          trackingRef.current = false;
          detachGestureListeners();
          return;
        }
        if (shouldIgnorePullGesture(dx, dy) || dy <= 0) {
          return;
        }
        trackingRef.current = true;
      }

      if (dy <= 0) {
        trackingRef.current = false;
        pullDistanceRef.current = 0;
        syncPullToRefreshSurface(0, false, false, thresholdPx);
        setPullDistance(0);
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      pullDistanceRef.current = dy;
      syncPullToRefreshSurface(dy, false, false, thresholdPx);
      setPullDistance(dy);
    }

    function finishGesture() {
      detachGestureListeners();

      if (isRefreshingRef.current) {
        return;
      }

      const distance = pullDistanceRef.current;
      const physics = getPullToRefreshPhysics(distance, thresholdPx);
      startPointRef.current = null;
      trackingRef.current = false;

      if (!physics.isReady || distance <= 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        if (distance > 0) {
          beginSettle();
        }
        return;
      }

      isRefreshingRef.current = true;
      setIsRefreshing(true);
      syncPullToRefreshSurface(thresholdPx, true, false, thresholdPx);
      const refreshStartedAt = Date.now();

      void Promise.resolve()
        .then(() => onRefreshRef.current())
        .catch(() => undefined)
        .then(() => {
          const remaining = remainingPullToRefreshHoldMs(
            refreshStartedAt,
            Date.now(),
          );
          if (remaining <= 0) {
            return;
          }
          return new Promise<void>((resolve) => {
            window.setTimeout(resolve, remaining);
          });
        })
        .finally(() => {
          isRefreshingRef.current = false;
          pullDistanceRef.current = 0;
          setIsRefreshing(false);
          setPullDistance(0);
          beginSettle();
        });
    }

    window.addEventListener("touchstart", onTouchStart, { passive: true });

    return () => {
      detachGestureListeners();
      window.removeEventListener("touchstart", onTouchStart);
    };
  }, [beginSettle, clearSettleTimer, enabled, resetPull, thresholdPx]);

  const physics = getPullToRefreshPhysics(
    isRefreshing ? thresholdPx : pullDistance,
    thresholdPx,
  );
  const status = getPullToRefreshStatus(
    isRefreshing,
    isRefreshing ? thresholdPx : pullDistance,
    thresholdPx,
  );

  return {
    status,
    offset: physics.offset,
    progress: physics.progress,
  };
}
